import React, { type ComponentType } from 'react';

import Loadable from 'react-loadable';

import IconTile from '@atlaskit/icon/icon-tile';
import type { IconTileProps } from '@atlaskit/icon/types';
import { ConfluenceIcon } from '@atlaskit/logo/confluence-icon';
import { JiraIcon } from '@atlaskit/logo/jira-icon';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { token } from '@atlaskit/tokens';

import { transformSmartLinkSizeToIconTileSize } from '../../../../../common/ui/icons/transform-smart-link-size-to-icon-tile-size';
import { IconType, SmartLinkSize } from '../../../../../constants';
import { getLazyIcons } from '../../../../../utils/get-lazy-icons';
import { isIconSizeLarge } from '../../../../../utils/is-icon-size-large';
import { getLazyIconComponent } from '../../../../../utils/lazy-component';
import { type AtlaskitIconProps } from './types';

const getIconImportFn = (icon: IconType, size: SmartLinkSize): (() => Promise<any>) | undefined => {
	const item = getLazyIcons()[icon];
	if (isIconSizeLarge(size) && item?.large) {
		return item.large;
	}
	return item?.default;
};

// Only used when platform_sl_replace_react_loadable is on. Kept separate from getIconImportFn so
// the gate-off path is unchanged.
const getLazyIcon = (icon: IconType, size: SmartLinkSize): ComponentType<any> | undefined => {
	const item = getLazyIcons()[icon];
	if (isIconSizeLarge(size) && item?.large) {
		return getLazyIconComponent(`${icon}:large`, item.large);
	}
	return item?.default ? getLazyIconComponent(`${icon}:default`, item.default) : undefined;
};

const importIcon = (importFn: () => Promise<any>): any => {
	return Loadable({
		loader: () => importFn().then((module) => module.default),
		loading: () => null,
	}) as any; // Because we're using dynamic loading here, TS will not be able to infer the type.
};

const getBlogIcon = () => require('../../../../../common/ui/icons/blog-icon').default;
const getDocumentIcon = () => require('../../../../../common/ui/icons/page-icon').default;
const getLiveDocumentIcon = () =>
	require('../../../../../common/ui/icons/live-document-icon').default;

const PRIORITY_ICONS = [
	IconType.PriorityBlocker,
	IconType.PriorityCritical,
	IconType.PriorityHigh,
	IconType.PriorityHighest,
	IconType.PriorityLow,
	IconType.PriorityLowest,
	IconType.PriorityMajor,
	IconType.PriorityMedium,
	IconType.PriorityMinor,
	IconType.PriorityTrivial,
];

const isCoreIcon = (icon: IconType): boolean => {
	return [
		IconType.Project,
		IconType.Template,
		IconType.Forbidden,
		IconType.Default,
		IconType.Error,
		IconType.Attachment,
		IconType.CheckItem,
		IconType.Component,
		IconType.Comment,
		IconType.View,
		IconType.React,
		IconType.Vote,
		IconType.PriorityUndefined,
		IconType.ProgrammingLanguage,
		IconType.Subscriber,
		IconType.SubTasksProgress,
		...PRIORITY_ICONS,
	].includes(icon);
};

const AtlaskitIcon = ({
	icon,
	label,
	testId,
	size = SmartLinkSize.Medium,
	isTiledIcon,
}: AtlaskitIconProps): React.JSX.Element | null => {
	// Intentionally keep these three icon types synchronous and outside lazy imports.
	// These icons should be present in SSR markup so hydration does not cause a
	// visible empty-state-then-pop-in transition in Smart Link consumers.
	switch (icon) {
		case IconType.Document: {
			const DocumentIcon = getDocumentIcon();
			return (
				<DocumentIcon
					label={label ?? 'document'}
					testId={testId}
					size={size}
					isTiledIcon={isTiledIcon}
				/>
			);
		}
		case IconType.Blog: {
			const BlogIcon = getBlogIcon();
			return (
				<BlogIcon label={label ?? 'blog'} testId={testId} size={size} isTiledIcon={isTiledIcon} />
			);
		}
		case IconType.LiveDocument: {
			const LiveDocumentIcon = getLiveDocumentIcon();
			return (
				<LiveDocumentIcon
					label={label ?? 'live-doc'}
					testId={testId}
					size={size}
					isTiledIcon={isTiledIcon}
				/>
			);
		}
	}

	let ImportedIcon: any;
	if (fg('platform_sl_replace_react_loadable')) {
		ImportedIcon = getLazyIcon(icon, size);
		if (!ImportedIcon) {
			return null;
		}
	} else {
		const importFn = getIconImportFn(icon, size);
		if (!importFn) {
			return null;
		}

		ImportedIcon = importIcon(importFn);
	}

	if (isCoreIcon(icon)) {
		let color;
		if (icon === IconType.Error || icon === IconType.Forbidden) {
			color = token('color.icon.danger');
		}
		switch (size) {
			case SmartLinkSize.Small:
			case SmartLinkSize.Medium:
				return <ImportedIcon label={label} testId={testId} color={color} />;
			case SmartLinkSize.Large:
			case SmartLinkSize.XLarge:
				if (PRIORITY_ICONS.includes(icon)) {
					return <ImportedIcon label={label} testId={testId} color={color} />;
				}

				let appearance: IconTileProps['appearance'];
				if (icon === IconType.Error || icon === IconType.Forbidden) {
					appearance = 'redBold';
				} else if (fg('platform_lp_non_bold_large_sl_icon')) {
					appearance = 'gray';
				} else {
					appearance = 'grayBold';
				}

				const iconTileSize = transformSmartLinkSizeToIconTileSize(size);
				if (iconTileSize) {
					return (
						<IconTile
							appearance={appearance}
							icon={
								isTiledIcon
									? (iconProps) => <ImportedIcon {...iconProps} spacing="spacious" />
									: ImportedIcon
							}
							size={iconTileSize}
							label={label ?? ''}
						/>
					);
				} else {
					return <ImportedIcon label={label} testId={testId} color={color} />;
				}
		}
	}

	switch (icon) {
		case IconType.Confluence:
			return (
				<ConfluenceIcon
					appearance="brand"
					testId={testId}
					label=""
					size={size === SmartLinkSize.Large || size === SmartLinkSize.XLarge ? 'small' : 'xxsmall'}
				/>
			);
		case IconType.Jira:
			return (
				<JiraIcon
					appearance="brand"
					testId={testId}
					label=""
					size={size === SmartLinkSize.Large || size === SmartLinkSize.XLarge ? 'small' : 'xxsmall'}
				/>
			);
		case IconType.Error:
		case IconType.Forbidden:
			return <ImportedIcon label={label} testId={testId} size={size} isTiledIcon={isTiledIcon} />;
		default:
			return <ImportedIcon label={label} testId={testId} size={size} isTiledIcon={isTiledIcon} />;
	}
};

export default AtlaskitIcon;
